
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { GlassCard, GlassCardHeader, GlassCardContent } from "@/components/ui/glass-card";
import { FormField, FormItem, FormLabel, FormControl, FormDescription } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Shield, Plus } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PPPCPermissions, PPPCService, PPPC_SERVICE_LABELS } from "@/types/application";
import { PrivacyPermissionItem } from "./PrivacyPermissionItem";

interface PPPCSettingsFormProps {
  form: UseFormReturn<any>;
}

export const PPPCSettingsForm = ({ form }: PPPCSettingsFormProps) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "pppcSettings.privacyPermissions"
  });

  const addNewPermission = () => {
    append({
      serviceIdentifier: "addressBook",
      allowed: true
    });
  };
  
  // Used services to avoid duplicates
  const usedServices = fields.map(field => 
    (field as unknown as PPPCPermissions).serviceIdentifier
  );
  
  // Available services for the dropdown
  const availableServices = Object.keys(PPPC_SERVICE_LABELS)
    .filter(service => !usedServices.includes(service as PPPCService))
    .map(service => ({
      value: service,
      label: PPPC_SERVICE_LABELS[service as PPPCService]
    }));

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Configure Privacy Preferences Policy Control (PPPC) settings for this macOS application.
        PPPC controls which system features and user data an application can access.
      </p>
      
      <GlassCard variant="default">
        <GlassCardHeader className="pb-2">
          <h3 className="text-lg font-medium">Application Identity</h3>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          <FormField
            control={form.control}
            name="pppcSettings.identifierType"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Identifier Type</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value || "bundleID"}
                    className="flex flex-row space-x-4"
                  >
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="bundleID" />
                      </FormControl>
                      <FormLabel className="font-normal">
                        Bundle ID
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <RadioGroupItem value="path" />
                      </FormControl>
                      <FormLabel className="font-normal">
                        Path
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormDescription>
                  Select how to identify the application
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pppcSettings.codeRequirement"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Code Requirement</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    value={field.value || ""}
                    placeholder="identifier &quot;com.example.app&quot; and anchor apple generic and..." 
                  />
                </FormControl>
                <FormDescription>
                  The code signature requirement for the application. You can get this using the command: 
                  <code className="ml-1 p-1 bg-slate-100 text-xs">codesign -dr - /Applications/ApplicationName.app</code>
                </FormDescription>
              </FormItem>
            )}
          />
        </GlassCardContent>
      </GlassCard>

      <GlassCard variant="default">
        <GlassCardHeader className="pb-2 flex flex-row items-center justify-between">
          <h3 className="text-lg font-medium">Privacy Permissions</h3>
          {availableServices.length > 0 && (
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={addNewPermission}
              className="h-8"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Permission
            </Button>
          )}
        </GlassCardHeader>
        <GlassCardContent>
          {fields.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>No privacy permissions have been added yet.</p>
              <Button 
                className="mt-4" 
                variant="outline" 
                size="sm" 
                onClick={addNewPermission}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Permission
              </Button>
            </div>
          ) : (
            <ScrollArea className="max-h-[300px] pr-4">
              <div className="space-y-6">
                {fields.map((field, index) => (
                  <PrivacyPermissionItem 
                    key={field.id}
                    index={index}
                    form={form}
                    onRemove={() => remove(index)}
                    availableServices={availableServices}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </GlassCardContent>
      </GlassCard>
    </div>
  );
};
